import {
  ItemLabels,
  LabelsInterface,
  RawGraphNode2,
  getComponentLabelParts,
  getOtherLabelsForComponent,
} from '@ardoq/graph';
import type { WithI18nLocale } from '@ardoq/i18n';

type GetGraphNodeOrGroupLabelInWorkspaceModeArgs = {
  labelsInterface: LabelsInterface;
  rawGraphItem: RawGraphNode2;
  isComponent: (modelId: string) => boolean;
};

type GetGraphNodeOrGroupLabelArgs =
  GetGraphNodeOrGroupLabelInWorkspaceModeArgs & WithI18nLocale;

/**
 * labels and sub-labels for groups are set in buildGroups and are already present
 * in the rawGraphItem.
 * In case of (isMultiLabel && isComponent), the main label is the component name,
 * otherwise the name set by the grouping.
 */
export const getGraphNodeOrGroupLabelInViewpointMode = ({
  labelsInterface,
  rawGraphItem,
  isComponent,
  i18nLocale,
}: GetGraphNodeOrGroupLabelArgs): ItemLabels => {
  const { modelId, subLabels, label } = rawGraphItem;

  const isComponentNode = modelId && isComponent(modelId);

  const mainNodeLabel = isComponentNode
    ? labelsInterface.getComponentDisplayName(modelId)
    : (label ?? '');

  const subLabel = subLabels?.join(', ');

  const otherLabels = isComponentNode
    ? getOtherLabelsForComponent(labelsInterface, modelId, i18nLocale)
    : [];
  return {
    mainLabel: mainNodeLabel,
    subLabel,
    otherLabels,
  };
};

/**
 * labels and sub-labels for groups are set in buildGroups and are already present
 * in the rawGraphItem.
 * In case of (isMultiLabel && isComponent), the main label is the component name,
 * otherwise the name set by the grouping.
 */
export const getGraphNodeOrGroupLabelInWorkspaceMode = ({
  labelsInterface,
  rawGraphItem,
  isComponent,
}: GetGraphNodeOrGroupLabelInWorkspaceModeArgs): ItemLabels => {
  const { modelId, subLabels, label } = rawGraphItem;

  const isComponentNode = modelId && isComponent(modelId);

  const labelParts = isComponentNode
    ? getComponentLabelParts(modelId, labelsInterface)
    : undefined;

  const mainNodeLabel = labelParts?.label ?? label ?? '';

  const legacyLabelParts = {
    label: mainNodeLabel,
    fieldLabel: labelParts?.fieldLabel ?? null,
    fieldValue: labelParts?.fieldValue ?? null,
  };

  const subLabel = subLabels?.join(', ');

  const { fieldLabel, fieldValue } = legacyLabelParts;
  const fieldLabelText = fieldLabel ? `${fieldLabel}: ` : '';
  const fieldValueText = fieldValue ? `${fieldValue}` : '';

  const mainLabel =
    (fieldValue
      ? `${mainNodeLabel} [${fieldLabelText}${fieldValueText}]`
      : mainNodeLabel) ?? '';

  return {
    mainLabel,
    subLabel,
    legacyLabelParts,
  };
};
